import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Burç bilgileri - isim kısaltmaları ve element renkleri
const ZODIAC_SIGNS = [
  { name: "Aries", abbr: "KOÇ", tr: "Koç", color: "#FF6B6B", element: "fire" },
  { name: "Taurus", abbr: "BOĞ", tr: "Boğa", color: "#7BC67E", element: "earth" },
  { name: "Gemini", abbr: "İKİ", tr: "İkizler", color: "#FFD93D", element: "air" },
  { name: "Cancer", abbr: "YEN", tr: "Yengeç", color: "#6CB4EE", element: "water" },
  { name: "Leo", abbr: "ASL", tr: "Aslan", color: "#FF9F43", element: "fire" },
  { name: "Virgo", abbr: "BAŞ", tr: "Başak", color: "#A8E6CF", element: "earth" },
  { name: "Libra", abbr: "TER", tr: "Terazi", color: "#FECA57", element: "air" },
  { name: "Scorpio", abbr: "AKR", tr: "Akrep", color: "#E8475F", element: "water" },
  { name: "Sagittarius", abbr: "YAY", tr: "Yay", color: "#E056A0", element: "fire" },
  { name: "Capricorn", abbr: "OĞL", tr: "Oğlak", color: "#9B8EC4", element: "earth" },
  { name: "Aquarius", abbr: "KOV", tr: "Kova", color: "#63CDDA", element: "air" },
  { name: "Pisces", abbr: "BAL", tr: "Balık", color: "#778BEB", element: "water" },
];

// Element renkleri
const ELEMENT_COLORS = {
  fire: { from: "#FF6B6B", to: "#FF9F43", bg: "rgba(255,107,107,0.08)" },
  earth: { from: "#7BC67E", to: "#A8E6CF", bg: "rgba(123,198,126,0.08)" },
  air: { from: "#FFD93D", to: "#FECA57", bg: "rgba(255,217,61,0.06)" },
  water: { from: "#6CB4EE", to: "#778BEB", bg: "rgba(108,180,238,0.08)" },
};

// Gezegen bilgileri
const PLANET_INFO = {
  Sun: { abbr: "GÜ", color: "#FFD93D", name: "Güneş" },
  Moon: { abbr: "AY", color: "#C8D6E5", name: "Ay" },
  Mercury: { abbr: "ME", color: "#A3CB38", name: "Merkür" },
  Venus: { abbr: "VE", color: "#FF6B81", name: "Venüs" },
  Mars: { abbr: "MA", color: "#FF4757", name: "Mars" },
  Jupiter: { abbr: "JÜ", color: "#FFA502", name: "Jüpiter" },
  Saturn: { abbr: "SA", color: "#9B8EC4", name: "Satürn" },
  Uranus: { abbr: "UR", color: "#63CDDA", name: "Uranüs" },
  Neptune: { abbr: "NE", color: "#5352ED", name: "Neptün" },
  Pluto: { abbr: "PL", color: "#A0896B", name: "Plüton" },
};

// Açı tipleri
const ASPECT_TYPES = {
  conjunction: { angle: 0, orb: 8, color: "#FFD93D", name: "Kavuşum", dash: "" },
  sextile: { angle: 60, orb: 6, color: "#7BC67E", name: "Sekstil", dash: "4,3" },
  square: { angle: 90, orb: 8, color: "#FF4757", name: "Kare", dash: "" },
  trine: { angle: 120, orb: 8, color: "#74B9FF", name: "Üçgen", dash: "" },
  opposition: { angle: 180, orb: 8, color: "#E8475F", name: "Karşıt", dash: "6,4" },
};

// Yardımcı fonksiyonlar
const toRad = (deg) => (deg * Math.PI) / 180;

const getPoint = (cx, cy, radius, angleDeg) => {
  const rad = toRad(angleDeg - 90);
  return {
    x: cx + radius * Math.cos(rad),
    y: cy + radius * Math.sin(rad),
  };
};

// Dereceyi burç içi dereceye çevir
const degreeToZodiacDegree = (absDegreee) => {
  const signIndex = Math.floor(absDegreee / 30);
  const degInSign = absDegreee % 30;
  return { signIndex, degInSign: degInSign.toFixed(1) };
};

// Gezegenler arası açıları hesapla
const calculateAspects = (planetPositions) => {
  const aspects = [];
  const entries = Object.entries(planetPositions);

  for (let i = 0; i < entries.length; i++) {
    for (let j = i + 1; j < entries.length; j++) {
      const [name1, deg1] = entries[i];
      const [name2, deg2] = entries[j];
      const diff = Math.abs(deg1 - deg2);
      const angle = diff > 180 ? 360 - diff : diff;

      for (const [type, spec] of Object.entries(ASPECT_TYPES)) {
        if (Math.abs(angle - spec.angle) <= spec.orb) {
          aspects.push({
            planet1: name1,
            planet2: name2,
            type,
            ...spec,
            exactAngle: angle,
          });
          break;
        }
      }
    }
  }
  return aspects;
};

const BirthChart3D = ({ birthChartData, frontendChart }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [selectedPlanet, setSelectedPlanet] = useState(null);

  // SVG boyutları
  const size = 370;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size / 2 - 10;
  const zodiacOuterR = outerR;
  const zodiacInnerR = outerR - 32;
  const houseR = zodiacInnerR;
  const planetR = zodiacInnerR - 30;
  const innerR = planetR - 25;
  const aspectR = innerR;

  // ===== VERİ İŞLEME =====
  // frontendChart varsa onu kullan (astrochart ile birebir aynı veriler)
  // yoksa birthChartData'dan hesapla
  const planetPositions = useMemo(() => {
    const positions = {};

    // Öncelik 1: frontendChart.planets (mutlak dereceler, en güvenilir)
    if (frontendChart?.planets) {
      Object.entries(frontendChart.planets).forEach(([name, value]) => {
        if (!PLANET_INFO[name]) return;
        const deg = Array.isArray(value) ? value[0] : value;
        positions[name] = deg;
      });
    }
    // Yedek: birthChartData.planets'tan hesapla
    else if (birthChartData?.planets) {
      const arr = Array.isArray(birthChartData.planets)
        ? birthChartData.planets
        : Object.values(birthChartData.planets);

      arr.forEach((p) => {
        if (!p?.planet_name || !PLANET_INFO[p.planet_name]) return;
        const sIdx = ZODIAC_SIGNS.findIndex(
          (z) => z.name === p.zodiac_sign || z.tr === p.zodiac_sign
        );
        positions[p.planet_name] = sIdx >= 0 ? sIdx * 30 + parseFloat(p.degree) : parseFloat(p.degree);
      });
    }

    return positions;
  }, [frontendChart, birthChartData]);

  // Ev cusps (derece pozisyonları)
  const houseCusps = useMemo(() => {
    // Öncelik 1: frontendChart.cusps (mutlak dereceler)
    if (frontendChart?.cusps && Array.isArray(frontendChart.cusps)) {
      return frontendChart.cusps;
    }
    // Yedek: birthChartData.houses'tan al
    if (birthChartData?.houses) {
      const arr = Array.isArray(birthChartData.houses)
        ? birthChartData.houses
        : Object.values(birthChartData.houses);
      return arr
        .sort((a, b) => a.house_number - b.house_number)
        .map((h) => parseFloat(h.start_degree));
    }
    return [];
  }, [frontendChart, birthChartData]);

  // Ek gezegen bilgileri (burç, ev, retrograde)
  const planetDetails = useMemo(() => {
    const details = {};
    if (birthChartData?.planets) {
      const arr = Array.isArray(birthChartData.planets)
        ? birthChartData.planets
        : Object.values(birthChartData.planets);
      arr.forEach((p) => {
        if (!p?.planet_name) return;
        details[p.planet_name] = {
          zodiac: p.zodiac_sign,
          house: p.house_number,
          retrograde: p.retrograde || false,
        };
      });
    }
    return details;
  }, [birthChartData]);

  // Aspect hesaplama
  const aspects = useMemo(() => {
    return calculateAspects(planetPositions);
  }, [planetPositions]);

  // Veri kontrolü
  if (Object.keys(planetPositions).length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-gray-400 text-sm">3D harita verisi yükleniyor...</p>
      </div>
    );
  }

  // Gezegen çakışma çözümü: birbirine çok yakın gezegenler offset alır
  const resolveOverlaps = (positions) => {
    const entries = Object.entries(positions);
    const resolved = {};
    const MIN_GAP = 8; // minimum derece farkı

    entries.forEach(([name, deg]) => {
      let offset = 0;
      for (const [otherName, otherDeg] of Object.entries(resolved)) {
        const diff = Math.abs(deg + offset - resolved[otherName]);
        const circDiff = diff > 180 ? 360 - diff : diff;
        if (circDiff < MIN_GAP) {
          offset += MIN_GAP - circDiff;
        }
      }
      resolved[name] = deg + offset;
    });
    return resolved;
  };

  const displayPositions = resolveOverlaps(planetPositions);

  return (
    <div className="bc3d-wrapper">
      {/* Başlık */}
      <div className="bc3d-header">
        <div className="bc3d-header-accent" />
        <h3 className="bc3d-header-title">Interaktif Doğum Haritası</h3>
        <p className="bc3d-header-subtitle">Jodiac'a özel • Gezegenlere dokunun</p>
      </div>

      {/* 3D Sahne */}
      <div
        className="bc3d-perspective"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={() => setIsHovered(true)}
        onTouchEnd={() => setTimeout(() => setIsHovered(false), 2000)}
      >
        <div className={`bc3d-scene ${isHovered ? "bc3d-scene--hover" : ""}`}>
          {/* Arka plan glow katmanları */}
          <div className="bc3d-bg-glow bc3d-bg-glow--primary" />
          <div className="bc3d-bg-glow bc3d-bg-glow--secondary" />

          <svg
            viewBox={`0 0 ${size} ${size}`}
            className="bc3d-canvas"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              {/* Merkez glow */}
              <radialGradient id="bc3d-center-glow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="rgba(61,167,181,0.12)" />
                <stop offset="100%" stopColor="rgba(61,167,181,0)" />
              </radialGradient>

              {/* Burç segment gradient'leri */}
              {ZODIAC_SIGNS.map((sign, i) => (
                <linearGradient
                  key={`zg-${i}`}
                  id={`bc3d-zg-${i}`}
                  x1="0%" y1="0%" x2="100%" y2="100%"
                >
                  <stop offset="0%" stopColor={ELEMENT_COLORS[sign.element].from} stopOpacity="0.18" />
                  <stop offset="100%" stopColor={ELEMENT_COLORS[sign.element].to} stopOpacity="0.05" />
                </linearGradient>
              ))}

              {/* Glow filtreleri */}
              <filter id="bc3d-soft-glow" x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="1.5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>

              <filter id="bc3d-strong-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>

              <filter id="bc3d-line-glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="1" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* ===== BURÇ HALKASI (Outer Ring) ===== */}
            {/* Dış çizgi */}
            <circle
              cx={cx} cy={cy} r={zodiacOuterR}
              fill="none"
              stroke="rgba(61,167,181,0.25)"
              strokeWidth="1.5"
              className="bc3d-anim-ring"
            />

            {/* Burç segmentleri */}
            {ZODIAC_SIGNS.map((sign, i) => {
              const startAng = i * 30;
              const endAng = (i + 1) * 30;
              const pA = getPoint(cx, cy, zodiacOuterR, startAng);
              const pB = getPoint(cx, cy, zodiacOuterR, endAng);
              const pC = getPoint(cx, cy, zodiacInnerR, endAng);
              const pD = getPoint(cx, cy, zodiacInnerR, startAng);

              // Kısaltma etiketinin pozisyonu
              const mid = startAng + 15;
              const labelPos = getPoint(cx, cy, (zodiacOuterR + zodiacInnerR) / 2, mid);

              return (
                <g key={`z-${i}`} className="bc3d-zodiac-seg">
                  <path
                    d={`M ${pA.x} ${pA.y} A ${zodiacOuterR} ${zodiacOuterR} 0 0 1 ${pB.x} ${pB.y} L ${pC.x} ${pC.y} A ${zodiacInnerR} ${zodiacInnerR} 0 0 0 ${pD.x} ${pD.y} Z`}
                    fill={`url(#bc3d-zg-${i})`}
                    stroke="rgba(61,167,181,0.12)"
                    strokeWidth="0.5"
                  />
                  {/* Segment ayırıcı çizgi */}
                  <line
                    x1={pA.x} y1={pA.y} x2={pD.x} y2={pD.y}
                    stroke="rgba(61,167,181,0.15)"
                    strokeWidth="0.5"
                  />
                  {/* Burç kısaltması */}
                  <text
                    x={labelPos.x}
                    y={labelPos.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={sign.color}
                    fontSize="7.5"
                    fontWeight="800"
                    fontFamily="'Inter', 'Segoe UI', sans-serif"
                    letterSpacing="0.5"
                    className="bc3d-zodiac-label"
                  >
                    {sign.abbr}
                  </text>
                </g>
              );
            })}

            {/* İç burç çizgisi */}
            <circle
              cx={cx} cy={cy} r={zodiacInnerR}
              fill="none"
              stroke="rgba(61,167,181,0.3)"
              strokeWidth="1"
              className="bc3d-anim-ring bc3d-anim-ring--delay1"
            />

            {/* ===== EV ÇİZGİLERİ ===== */}
            {houseCusps.map((cuspDeg, i) => {
              const houseNum = i + 1;
              const pOut = getPoint(cx, cy, zodiacInnerR, cuspDeg);
              const pIn = getPoint(cx, cy, aspectR, cuspDeg);

              // Ana eksen mi?
              const isAxis = [1, 4, 7, 10].includes(houseNum);

              // Ev numarası pozisyonu (iki cusp'ın ortası)
              const nextCusp = houseCusps[(i + 1) % 12];
              let midAng;
              if (nextCusp < cuspDeg) {
                midAng = (cuspDeg + (nextCusp + 360)) / 2;
                if (midAng >= 360) midAng -= 360;
              } else {
                midAng = (cuspDeg + nextCusp) / 2;
              }
              const numPos = getPoint(cx, cy, zodiacInnerR - 14, midAng);

              // Ev cusp derece etiketi
              const cuspLabel = degreeToZodiacDegree(cuspDeg);
              const degLabelPos = getPoint(cx, cy, zodiacInnerR + 3, cuspDeg);
              // Derece etiketinin metin açısı
              const textRotation = cuspDeg;

              return (
                <g key={`h-${i}`}>
                  {/* Ev çizgisi */}
                  <line
                    x1={pOut.x} y1={pOut.y}
                    x2={pIn.x} y2={pIn.y}
                    stroke={isAxis ? "rgba(61,167,181,0.55)" : "rgba(61,167,181,0.18)"}
                    strokeWidth={isAxis ? "1.5" : "0.7"}
                    className="bc3d-house-line"
                    filter={isAxis ? "url(#bc3d-line-glow)" : undefined}
                  />
                  {/* Ev numarası */}
                  <text
                    x={numPos.x} y={numPos.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="rgba(61,167,181,0.55)"
                    fontSize="7.5"
                    fontWeight="700"
                    fontFamily="'Inter', 'Segoe UI', sans-serif"
                  >
                    {houseNum}
                  </text>
                  {/* Ev cusp derecesi (ana eksenler için) */}
                  {isAxis && (
                    <text
                      x={degLabelPos.x} y={degLabelPos.y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill="rgba(61,167,181,0.35)"
                      fontSize="5"
                      fontWeight="600"
                      fontFamily="'Inter', sans-serif"
                      transform={`rotate(${textRotation}, ${degLabelPos.x}, ${degLabelPos.y})`}
                    >
                      {cuspLabel.degInSign}°
                    </text>
                  )}
                </g>
              );
            })}

            {/* İç halka (aspect alanı sınırı) */}
            <circle
              cx={cx} cy={cy} r={aspectR}
              fill="none"
              stroke="rgba(61,167,181,0.18)"
              strokeWidth="0.8"
              className="bc3d-anim-ring bc3d-anim-ring--delay2"
            />

            {/* Merkez glow */}
            <circle cx={cx} cy={cy} r={aspectR} fill="url(#bc3d-center-glow)" />

            {/* ===== ASPECT ÇİZGİLERİ ===== */}
            {aspects.map((asp, i) => {
              const deg1 = planetPositions[asp.planet1];
              const deg2 = planetPositions[asp.planet2];
              if (deg1 === undefined || deg2 === undefined) return null;

              const p1 = getPoint(cx, cy, aspectR - 4, deg1);
              const p2 = getPoint(cx, cy, aspectR - 4, deg2);

              return (
                <line
                  key={`asp-${i}`}
                  x1={p1.x} y1={p1.y}
                  x2={p2.x} y2={p2.y}
                  stroke={asp.color}
                  strokeWidth="1.2"
                  strokeOpacity="0.55"
                  strokeDasharray={asp.dash || undefined}
                  filter="url(#bc3d-line-glow)"
                  className="bc3d-aspect-line"
                />
              );
            })}

            {/* ===== GEZEGENLER ===== */}
            {Object.entries(planetPositions).map(([name, realDeg]) => {
              const displayDeg = displayPositions[name] || realDeg;
              const pos = getPoint(cx, cy, planetR, displayDeg);
              const info = PLANET_INFO[name];
              const detail = planetDetails[name];
              if (!info) return null;

              const isSelected = selectedPlanet === name;
              const zodiacDeg = degreeToZodiacDegree(realDeg);

              // Derece etiketi pozisyonu (gezegenin dışında)
              const degLabelPos = getPoint(cx, cy, planetR + 15, displayDeg);

              return (
                <g
                  key={`p-${name}`}
                  className={`bc3d-planet ${isSelected ? "bc3d-planet--active" : ""}`}
                  onClick={() => setSelectedPlanet(isSelected ? null : name)}
                  style={{ cursor: "pointer" }}
                >
                  {/* Aura */}
                  <circle
                    cx={pos.x} cy={pos.y}
                    r={isSelected ? 16 : 11}
                    fill={info.color}
                    fillOpacity={isSelected ? 0.18 : 0.06}
                    className="bc3d-planet-aura"
                  />
                  {/* Gezegen dairesi */}
                  <circle
                    cx={pos.x} cy={pos.y}
                    r={9}
                    fill="rgba(15,15,25,0.8)"
                    stroke={info.color}
                    strokeWidth="1.5"
                    filter="url(#bc3d-strong-glow)"
                  />
                  {/* Gezegen kısaltması */}
                  <text
                    x={pos.x} y={pos.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={info.color}
                    fontSize="7"
                    fontWeight="900"
                    fontFamily="'Inter', 'Segoe UI', sans-serif"
                    letterSpacing="0.3"
                  >
                    {info.abbr}
                  </text>
                  {/* Derece etiketi (her zaman görünür) */}
                  <text
                    x={degLabelPos.x} y={degLabelPos.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={info.color}
                    fillOpacity="0.6"
                    fontSize="5.5"
                    fontWeight="700"
                    fontFamily="'Inter', sans-serif"
                  >
                    {zodiacDeg.degInSign}°
                  </text>
                </g>
              );
            })}

            {/* Merkez noktası */}
            <circle cx={cx} cy={cy} r={3.5} fill="rgba(61,167,181,0.25)" className="bc3d-center-dot" />
            <circle cx={cx} cy={cy} r={1.2} fill="rgba(61,167,181,0.7)" />
          </svg>
        </div>
      </div>

      {/* Seçili Gezegen Bilgi Kartı */}
      <AnimatePresence>
        {selectedPlanet && PLANET_INFO[selectedPlanet] && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="bc3d-info-card"
          >
            <div className="bc3d-info-card-inner">
              <div
                className="bc3d-info-badge"
                style={{ backgroundColor: `${PLANET_INFO[selectedPlanet].color}15`, borderColor: `${PLANET_INFO[selectedPlanet].color}30` }}
              >
                <span style={{ color: PLANET_INFO[selectedPlanet].color }}>
                  {PLANET_INFO[selectedPlanet].abbr}
                </span>
              </div>
              <div className="bc3d-info-text">
                <div className="bc3d-info-name">{PLANET_INFO[selectedPlanet].name}</div>
                <div className="bc3d-info-detail">
                  {degreeToZodiacDegree(planetPositions[selectedPlanet]).degInSign}°{" "}
                  {planetDetails[selectedPlanet]?.zodiac || ZODIAC_SIGNS[degreeToZodiacDegree(planetPositions[selectedPlanet]).signIndex]?.tr}
                  {" • "}{planetDetails[selectedPlanet]?.house || "?"}. Ev
                  {planetDetails[selectedPlanet]?.retrograde && " • ℞ Retro"}
                </div>
              </div>
              <button
                className="bc3d-info-close"
                onClick={(e) => { e.stopPropagation(); setSelectedPlanet(null); }}
              >
                ×
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Açı Lejantı */}
      <div className="bc3d-legend">
        {Object.entries(ASPECT_TYPES).map(([key, asp]) => (
          <div key={key} className="bc3d-legend-chip">
            <svg width="14" height="8" viewBox="0 0 14 8">
              <line
                x1="0" y1="4" x2="14" y2="4"
                stroke={asp.color}
                strokeWidth="2"
                strokeDasharray={asp.dash || undefined}
              />
            </svg>
            <span className="bc3d-legend-label">{asp.name}</span>
          </div>
        ))}
      </div>

      <style>{`
        .bc3d-wrapper {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 14px;
          padding: 0 4px 20px;
        }

        /* === Başlık === */
        .bc3d-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          position: relative;
        }
        .bc3d-header-accent {
          width: 28px;
          height: 3px;
          border-radius: 2px;
          background: linear-gradient(90deg, #3DA7B5, #5ABCC8);
          margin-bottom: 4px;
        }
        .bc3d-header-title {
          font-size: 16px;
          font-weight: 800;
          color: #1a1a2e;
          margin: 0;
          letter-spacing: -0.4px;
        }
        .bc3d-header-subtitle {
          font-size: 10px;
          color: rgba(0,0,0,0.35);
          margin: 0;
          font-weight: 600;
          letter-spacing: 0.2px;
        }

        /* === 3D Perspective === */
        .bc3d-perspective {
          perspective: 800px;
          width: 100%;
          max-width: 380px;
          display: flex;
          justify-content: center;
        }

        .bc3d-scene {
          position: relative;
          width: 100%;
          padding: 8px;
          transform: rotateX(20deg) rotateY(0deg);
          transform-style: preserve-3d;
          transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1);
          animation: bc3d-orbit 10s ease-in-out infinite;
        }
        .bc3d-scene--hover {
          transform: rotateX(8deg) rotateY(6deg) scale(1.02);
          animation-play-state: paused;
        }

        .bc3d-canvas {
          width: 100%;
          height: auto;
          filter: drop-shadow(0 16px 32px rgba(61,167,181,0.1));
        }

        /* === Arka Plan Glow === */
        .bc3d-bg-glow {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          z-index: -1;
        }
        .bc3d-bg-glow--primary {
          width: 55%;
          height: 55%;
          top: 22%;
          left: 22%;
          background: radial-gradient(circle, rgba(61,167,181,0.1) 0%, transparent 70%);
          animation: bc3d-glow-breathe 5s ease-in-out infinite;
        }
        .bc3d-bg-glow--secondary {
          width: 75%;
          height: 75%;
          top: 12%;
          left: 12%;
          background: radial-gradient(circle, rgba(119,139,235,0.05) 0%, transparent 70%);
          animation: bc3d-glow-breathe 7s ease-in-out infinite reverse;
        }

        /* === Halka Animasyonları === */
        .bc3d-anim-ring {
          animation: bc3d-ring-breathe 5s ease-in-out infinite;
        }
        .bc3d-anim-ring--delay1 {
          animation-delay: 0.6s;
        }
        .bc3d-anim-ring--delay2 {
          animation-delay: 1.2s;
        }

        /* === Burç Segmentleri === */
        .bc3d-zodiac-seg path {
          transition: all 0.3s ease;
        }
        .bc3d-zodiac-seg:hover path {
          fill-opacity: 0.35;
        }
        .bc3d-zodiac-label {
          pointer-events: none;
          transition: font-size 0.3s ease;
        }

        /* === Ev Çizgileri === */
        .bc3d-house-line {
          transition: stroke-opacity 0.3s ease;
        }

        /* === Aspect Çizgileri === */
        .bc3d-aspect-line {
          animation: bc3d-aspect-glow 4s ease-in-out infinite;
        }

        /* === Gezegenler === */
        .bc3d-planet {
          transition: opacity 0.3s ease;
        }
        .bc3d-planet-aura {
          transition: all 0.4s ease;
          animation: bc3d-planet-breathe 4s ease-in-out infinite;
        }
        .bc3d-planet--active .bc3d-planet-aura {
          animation: bc3d-planet-active 2s ease-in-out infinite;
        }

        /* === Merkez Nokta === */
        .bc3d-center-dot {
          animation: bc3d-center-throb 3s ease-in-out infinite;
        }

        /* === Bilgi Kartı === */
        .bc3d-info-card {
          width: 100%;
          max-width: 320px;
        }
        .bc3d-info-card-inner {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          background: linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(245,250,251,0.96) 100%);
          backdrop-filter: blur(16px);
          border-radius: 16px;
          border: 1px solid rgba(61,167,181,0.12);
          box-shadow: 0 4px 24px rgba(61,167,181,0.06);
        }
        .bc3d-info-badge {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          border: 1.5px solid;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 900;
          flex-shrink: 0;
        }
        .bc3d-info-text {
          flex: 1;
          min-width: 0;
        }
        .bc3d-info-name {
          font-size: 14px;
          font-weight: 800;
          color: #1a1a2e;
          line-height: 1.2;
        }
        .bc3d-info-detail {
          font-size: 11px;
          color: rgba(0,0,0,0.45);
          font-weight: 600;
          margin-top: 2px;
        }
        .bc3d-info-close {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: none;
          background: rgba(0,0,0,0.05);
          color: rgba(0,0,0,0.3);
          font-size: 16px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.2s;
        }
        .bc3d-info-close:hover {
          background: rgba(0,0,0,0.1);
          color: rgba(0,0,0,0.5);
        }

        /* === Lejant === */
        .bc3d-legend {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 6px;
        }
        .bc3d-legend-chip {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          border-radius: 20px;
          background: rgba(0,0,0,0.03);
          border: 1px solid rgba(0,0,0,0.04);
        }
        .bc3d-legend-label {
          font-size: 9px;
          color: rgba(0,0,0,0.4);
          font-weight: 700;
          letter-spacing: 0.2px;
        }

        /* ===== ANİMASYONLAR ===== */

        @keyframes bc3d-orbit {
          0%, 100% { transform: rotateX(20deg) rotateY(0deg); }
          20%      { transform: rotateX(16deg) rotateY(4deg); }
          40%      { transform: rotateX(22deg) rotateY(-2deg); }
          60%      { transform: rotateX(17deg) rotateY(5deg); }
          80%      { transform: rotateX(21deg) rotateY(-3deg); }
        }

        @keyframes bc3d-ring-breathe {
          0%, 100% { stroke-opacity: 1; }
          50%      { stroke-opacity: 0.45; }
        }

        @keyframes bc3d-glow-breathe {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.4; transform: scale(1.08); }
        }

        @keyframes bc3d-planet-breathe {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.35; }
        }

        @keyframes bc3d-planet-active {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.5; }
        }

        @keyframes bc3d-center-throb {
          0%, 100% { opacity: 0.25; }
          50%      { opacity: 0.6; }
        }

        @keyframes bc3d-aspect-glow {
          0%, 100% { stroke-opacity: 0.55; }
          50%      { stroke-opacity: 0.25; }
        }
      `}</style>
    </div>
  );
};

export default BirthChart3D;
